import { Node, mergeAttributes } from '@tiptap/core';
import { InlineMediaType } from './media-types';
export { mediaType } from './media-types';
import { PlaceholderStatus } from './placeholder-status';

export enum InlineMediaNode {
  Generic = 'imGenericMedia',
  GenericPlaceholder = 'imGenericMediaPlaceholder',
}

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    [InlineMediaNode.Generic]: {
      setGenericMedia: (options: {
        fileName: string;
        url: string;
        fileType: string;
        mediaType: string;
      }) => ReturnType;
    };
  }
}

export const GenericMediaNode = Node.create({
  name: InlineMediaNode.Generic,
  group: 'block',
  selectable: false,
  atom: true,

  addAttributes() {
    return {
      fileName: {
        default: 'Loading...',
        parseHTML: (element) => {
          return element.getAttribute('fileName') ?? element.getAttribute('filename');
        },
      },
      url: {
        default: '',
      },
      fileType: {
        default: null,
        parseHTML: (element) => {
          return element.getAttribute('fileType') ?? element.getAttribute('filetype');
        },
      },
      mediaType: {
        default: InlineMediaType.File,
        parseHTML: (element) => {
          return element.getAttribute('mediaType') ?? element.getAttribute('mediatype');
        },
      },
    };
  },

  parseHTML() {
    return [
      {
        tag: 'generic-media',
      },
    ];
  },

  renderHTML({ HTMLAttributes }) {
    /**
     * If the necessary attributes are not available, fall back to the old
     * structure, which won't throw.
     */
    if (
      !(
        typeof HTMLAttributes.url === 'string' &&
        typeof HTMLAttributes.fileName === 'string' &&
        typeof HTMLAttributes.mediaType === 'string'
      )
    ) {
      return ['generic-media', mergeAttributes(HTMLAttributes)];
    }

    switch (HTMLAttributes.mediaType) {
      case InlineMediaType.Video:
        return [
          'generic-media',
          mergeAttributes(HTMLAttributes),
          [
            'div',
            { class: 'generic-video-file-wrapper' },
            ['video', { controls: '' }, ['source', { src: HTMLAttributes.url }]],
          ],
        ];
      case InlineMediaType.File:
        return [
          'generic-media',
          mergeAttributes(HTMLAttributes),
          [
            'span',
            { class: 'generic-media-file-wrapper' },
            ['span', { class: 'media-thumbnail' }, ['span', { class: 'custom-icons' }]],
            [
              'span',
              { class: 'generic-file-content' },
              ['div', { class: 'filename' }, HTMLAttributes.fileName],
              [
                'a',
                { class: 'filelink', target: '_blank', rel: 'noopener', href: HTMLAttributes.url },
                'Click to download',
              ],
            ],
          ],
        ];
      default:
        return ['generic-media', mergeAttributes(HTMLAttributes)];
    }
  },

  addCommands() {
    return {
      setGenericMedia:
        (options) =>
        ({ commands }) => {
          return commands.insertContent({
            type: this.name,
            attrs: {
              ...options,
              mediaType: options.mediaType ?? InlineMediaType.File,
            },
          });
        },
    };
  },
});

export const GenericMediaPlaceholder = Node.create({
  name: InlineMediaNode.GenericPlaceholder,
  group: 'block',
  selectable: true,
  atom: true,
  addAttributes() {
    return {
      uploadId: {
        default: 'none',
      },
      fileName: {
        default: 'Loading...',
      },
      fileType: {
        default: undefined,
      },
      mediaType: {
        default: InlineMediaType.File,
      },
      uploadProgress: {
        default: 0,
      },
      status: {
        default: PlaceholderStatus.Active,
      },
    };
  },

  parseHTML() {
    return [
      {
        tag: 'generic-media-placeholder',
      },
    ];
  },

  renderHTML({ HTMLAttributes }) {
    return ['generic-media-placeholder', mergeAttributes(HTMLAttributes)];
  },
});

export { InlineMediaType, PlaceholderStatus };
